import { TrackingPageGameBlock } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import kaplay, { KAPLAYCtx } from "kaplay";
import { useEffect, useState } from "react";
import { CustomSprite, GameState, TrackingPageGame } from "../game";
import { GameCompleteCallback, GameControls } from "../game-controls";

function initializeGame({
  canvas,
  game,
  blockProps,
  onGameComplete,
  playerSprite,
  npcSprite,
}: {
  canvas: HTMLCanvasElement;
  game: TrackingPageGame;
  blockProps: TrackingPageGameBlock;
  onGameComplete: GameCompleteCallback;
  playerSprite?: CustomSprite | null;
  npcSprite?: CustomSprite | null;
}): { controls: GameControls; kaplayCtx: KAPLAYCtx } {
  const k = kaplay({ canvas, background: [0, 0, 0] });

  const controls = game.initializeGame(
    k,
    onGameComplete,
    blockProps,
    playerSprite,
    npcSprite,
  );

  return { controls, kaplayCtx: k };
}

export function useGameInitialization(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  game: TrackingPageGame,
  blockProps: TrackingPageGameBlock,
  gameState: GameState,
  gameInitialized: React.MutableRefObject<boolean>,
  kaplayInstance: React.MutableRefObject<KAPLAYCtx | null>,
  resizeObserverRef: React.MutableRefObject<ResizeObserver | null>,
  onGameComplete: GameCompleteCallback,
  playerSprite?: CustomSprite | null,
  npcSprite?: CustomSprite | null,
) {
  const [gameControls, setGameControls] = useState<GameControls | null>(null);

  useEffect(() => {
    if (
      canvasRef.current &&
      !gameInitialized.current &&
      gameState === GameState.Gameplay
    ) {
      gameInitialized.current = true;
      const { controls, kaplayCtx } = initializeGame({
        canvas: canvasRef.current,
        game,
        blockProps,
        onGameComplete,
        playerSprite,
        npcSprite,
      });
      kaplayInstance.current = kaplayCtx;
      setGameControls(controls);

      if (canvasRef.current && controls.onResize) {
        resizeObserverRef.current = new ResizeObserver(() => {
          controls.onResize?.();
        });
        resizeObserverRef.current.observe(canvasRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [resizeObserverRef]);

  return { gameControls, setGameControls };
}
